import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { useCountUp } from "@/hooks/useCountUp";
import { TransitionLink } from "@/components/ui/TransitionLink";
import { ModelLockup } from "@/components/ui/ModelLockup";
import { GALLERY_IMAGES } from "@/data/gallery";
import { PRODUCT_CATEGORIES } from "@/data/products";
import { BIKE_LINE_BRANDING } from "@/data/bikeLineBranding";
import { isComingSoonFrame } from "@/data/availability";
import { useContent } from "@/content/ContentContext";
import { CraftSection } from "@/components/sections/CraftSection";
import { ManifestoSection } from "@/components/sections/ManifestoSection";
import { CommissionJourneySection } from "@/components/sections/CommissionJourneySection";

const reveal = (inView: boolean) =>
  `transition-all duration-1000 ease-out-expo ${inView ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-sm"}`;

/** Finite stagger classes — indexed by list position to avoid inline styles. */
const STAGGER = [
  "[transition-delay:0ms]",
  "[transition-delay:90ms]",
  "[transition-delay:180ms]",
  "[transition-delay:270ms]",
  "[transition-delay:360ms]",
  "[transition-delay:450ms]",
  "[transition-delay:540ms]",
  "[transition-delay:630ms]",
];

function CornerBrackets() {
  return (
    <>
      <span aria-hidden className="pointer-events-none absolute top-6 left-6 w-8 h-8 border-t border-l border-gold-500/30" />
      <span aria-hidden className="pointer-events-none absolute top-6 right-6 w-8 h-8 border-t border-r border-gold-500/30" />
      <span aria-hidden className="pointer-events-none absolute bottom-6 left-6 w-8 h-8 border-b border-l border-gold-500/30" />
      <span aria-hidden className="pointer-events-none absolute bottom-6 right-6 w-8 h-8 border-b border-r border-gold-500/30" />
    </>
  );
}

const HERO_SPECS = [
  { key: "REF", value: "BB-2026 / SERIES I" },
  { key: "MATERIAL", value: "Ti-3AL-2.5V GRADE 9" },
  { key: "CONSTRUCTION", value: "ADDITIVE + FILAMENT-WOUND" },
  { key: "GEOMETRY", value: "CUSTOM-FIT" },
  { key: "ORIGIN", value: "37.90°S 175.47°E" },
];

const HERO_SPEC_DELAYS = [
  "[animation-delay:1s]",
  "[animation-delay:1.12s]",
  "[animation-delay:1.24s]",
  "[animation-delay:1.36s]",
  "[animation-delay:1.48s]",
];

/**
 * The hero entry animations replay on every mount, blanking the hero when the
 * user navigates back to the page. Play them once per session; on return
 * visits the hero renders in its final state (ambient loops still run).
 */
let heroIntroPlayed = false;

function HeroSection() {
  const { content } = useContent();
  const { home } = content;
  const introPlayed = heroIntroPlayed;

  useEffect(() => {
    heroIntroPlayed = true;
  }, []);

  const intro = (classes: string) => (introPlayed ? "" : classes);

  return (
    <section className="relative min-h-screen bg-carbon-900 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={home.heroImage}
          alt=""
          aria-hidden
          className="h-full w-full object-cover object-[65%_center] animate-kenburns motion-reduce:animate-none"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-carbon-900 from-25% via-carbon-900/70 via-50% to-carbon-900/5" />
      <div className="absolute inset-0 bg-gradient-to-b from-carbon-900/60 via-transparent to-carbon-900/40" />
      <CornerBrackets />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        <div className="flex flex-col justify-center px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24 3xl:px-32 py-28">
          <p className={`text-gold-500 font-mono text-xs tracking-[0.4em] uppercase mb-8 ${intro("animate-fade-in [animation-delay:0.15s]")}`}>
            {home.heroEyebrow}
          </p>

          <h1 className="font-display font-extrabold leading-[0.92] tracking-tight uppercase text-carbon-50 [font-stretch:125%] text-[2.75rem] md:text-[3.5rem] lg:text-[4.25rem] xl:text-[5.25rem] 2xl:text-[6.5rem] 3xl:text-[8.5rem]">
            <span className="block overflow-hidden pb-[0.08em]">
              <span className={`block ${intro("animate-mask-up [animation-delay:0.3s] motion-reduce:animate-fade-in")}`}>
                {home.heroTitle}
              </span>
            </span>
            <span className="block overflow-hidden pb-[0.08em]">
              <span className={`block ${intro("animate-mask-up [animation-delay:0.45s] motion-reduce:animate-fade-in")}`}>
                <span className="bg-gradient-gold bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer-slow motion-reduce:animate-none">
                  {home.heroAccent}
                </span>
              </span>
            </span>
          </h1>

          <div className={`divider-gold w-10 mt-7 mb-6 ${intro("animate-fade-in [animation-delay:0.7s]")}`} />

          <p className={`text-titanium-400 text-sm leading-relaxed max-w-[20rem] 2xl:text-base ${intro("animate-slide-up [animation-delay:0.85s]")}`}>
            {home.heroSubtitle}
          </p>

          <TransitionLink
            to={home.heroCtaLink}
            className={`group relative overflow-hidden self-start inline-flex items-center gap-4 border border-gold-500 text-carbon-50 font-display font-bold text-sm tracking-widest uppercase px-8 py-4 mt-10 ${intro("animate-fade-in [animation-delay:1s]")} transition-colors duration-500 ease-out-expo hover:text-carbon-900 before:absolute before:inset-0 before:bg-gold-500 before:-translate-x-full before:transition-transform before:duration-500 before:ease-out-expo hover:before:translate-x-0`}
          >
            <span className="relative z-10">{home.heroCtaText}</span>
            <span aria-hidden className="relative z-10 transition-transform duration-500 ease-out-expo group-hover:translate-x-1.5">
              -&gt;
            </span>
          </TransitionLink>
        </div>

        <div className="hidden lg:flex flex-col justify-end items-end pb-28 pr-16 xl:pr-20 2xl:pr-24 3xl:pr-32 gap-3">
          {HERO_SPECS.map(({ key, value }, i) => (
            <div
              key={key}
              className={`flex items-baseline gap-4 ${intro(`animate-slide-in-right ${HERO_SPEC_DELAYS[i]}`)}`}
            >
              <span className="font-mono text-2xs tracking-[0.3em] uppercase text-titanium-600">{key}</span>
              <span aria-hidden className="w-6 h-px bg-gold-500/40" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-titanium-400">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={`absolute bottom-8 left-10 md:left-14 lg:left-16 xl:left-20 2xl:left-24 3xl:left-32 flex items-center gap-3 ${intro("animate-fade-in [animation-delay:1.3s]")}`}>
        <div className="w-px h-12 overflow-hidden">
          <div className="w-px h-full bg-gradient-to-b from-gold-500/80 to-titanium-600 animate-scroll-line motion-reduce:animate-none" />
        </div>
        <p className="font-mono text-xs text-titanium-600 tracking-[0.3em] uppercase">Scroll</p>
      </div>
    </section>
  );
}

const MARQUEE_ITEMS = [
  "Grade 9 Titanium",
  "Additive Manufactured",
  "One Rider, One Machine",
  "Filament-Wound Carbon",
  "Geometry Without Compromise",
  "Hand-Built in New Zealand",
  "By Commission Only",
  "Every Frame, One of One",
];

function MarqueeStrip() {
  return (
    <section aria-hidden className="relative overflow-hidden border-y border-carbon-700 bg-carbon-950 py-5">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-carbon-950 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-carbon-950 to-transparent" />
      <div className="flex w-max animate-marquee motion-reduce:animate-none">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0 items-center">
            {MARQUEE_ITEMS.map((item) => (
              <span key={`${copy}-${item}`} className="flex items-center">
                <span className="font-display font-bold text-sm tracking-[0.3em] uppercase text-titanium-500 px-8 whitespace-nowrap">
                  {item}
                </span>
                <span className="w-1.5 h-1.5 rotate-45 bg-gold-500/60 shrink-0" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

const ABOUT_STATS = [
  { target: 1, prefix: "", suffix: "/1", label: "Every frame, one of one" },
  { target: 9, prefix: "GR", suffix: "", label: "Aerospace titanium" },
  { target: 100, prefix: "", suffix: "%", label: "Built to the rider" },
  { target: 12, prefix: "", suffix: "WK", label: "In the making" },
];

function StatBlock({
  target,
  prefix,
  suffix,
  label,
  start,
  index,
}: (typeof ABOUT_STATS)[number] & { start: boolean; index: number }) {
  const value = useCountUp(target, start);

  return (
    <div className={`flex flex-col gap-2 border-l border-carbon-700 pl-5 ${reveal(start)} ${STAGGER[index]}`}>
      <span className="font-display font-extrabold text-3xl md:text-4xl text-carbon-50 tabular-nums">
        <span className="text-gold-500">{prefix}</span>
        {value}
        <span className="text-gold-500 text-xl md:text-2xl">{suffix}</span>
      </span>
      <span className="font-mono text-2xs tracking-[0.25em] uppercase text-titanium-500">{label}</span>
    </div>
  );
}

function AboutSection() {
  const { ref, inView } = useInView<HTMLDivElement>(0.15, "home-about");
  const { content } = useContent();
  const { home } = content;

  return (
    <section id="about" className="relative py-32 overflow-hidden bg-carbon-900 texture-noise">
      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-6">
          <p className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 ${reveal(inView)} [transition-delay:0s]`}>
            {home.aboutEyebrow}
          </p>
          <h2 className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase leading-tight mb-8 ${reveal(inView)} [transition-delay:0.1s]`}>
            {home.aboutHeading}
            <br />
            <span className="text-titanium-400">{home.aboutSubheading}</span>
          </h2>
          <div className={`space-y-5 text-titanium-400 text-lg leading-relaxed ${reveal(inView)} [transition-delay:0.25s]`}>
            <p>{home.aboutBodyOne}</p>
            <p>{home.aboutBodyTwo}</p>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-10 mt-14">
            {ABOUT_STATS.map((stat, i) => (
              <StatBlock key={stat.label} {...stat} start={inView} index={i + 3} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div
            className={`relative aspect-[4/5] max-h-[36rem] w-full overflow-hidden transition-[clip-path,opacity] duration-[1.4s] ease-out-expo [transition-delay:0.2s] ${
              inView ? "opacity-100 [clip-path:inset(0_0_0_0)]" : "opacity-60 [clip-path:inset(8%_12%_8%_12%)]"
            }`}
          >
            <img
              src={home.aboutImage}
              alt=""
              aria-hidden
              className={`h-full w-full object-cover object-[75%_center] transition-transform duration-[1.8s] ease-out-expo ${inView ? "scale-100" : "scale-110"}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-carbon-900/70 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-4 font-mono text-2xs tracking-[0.3em] uppercase text-titanium-400">
              The Atelier / Cambridge, New Zealand
            </span>
          </div>
          <span aria-hidden className={`absolute -bottom-4 -left-4 w-24 h-24 border-b border-l border-gold-500/40 transition-opacity duration-1000 [transition-delay:0.8s] ${inView ? "opacity-100" : "opacity-0"}`} />
          <span aria-hidden className={`absolute -top-4 -right-4 w-24 h-24 border-t border-r border-gold-500/40 transition-opacity duration-1000 [transition-delay:0.8s] ${inView ? "opacity-100" : "opacity-0"}`} />
        </div>
      </div>
    </section>
  );
}

function GallerySection() {
  const { ref: headerRef, inView: headerIn } = useInView<HTMLDivElement>(0.15, "home-gallery-header");
  const { ref: gridRef, inView: gridIn } = useInView<HTMLDivElement>(0.05, "home-gallery-grid");

  return (
    <section id="gallery" className="py-32 bg-carbon-800">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={headerRef} className="flex items-end justify-between gap-6 mb-16">
          <div>
            <p className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 ${reveal(headerIn)} [transition-delay:0s]`}>
              The Portfolio
            </p>
            <h2 className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase ${reveal(headerIn)} [transition-delay:0.1s]`}>
              Recent commissions.
            </h2>
          </div>
          <span className={`hidden md:block font-mono text-2xs tracking-[0.3em] uppercase text-titanium-600 pb-2 ${reveal(headerIn)} [transition-delay:0.2s]`}>
            Selected works / 01&ndash;05
          </span>
        </div>

        <div ref={gridRef} className="grid grid-cols-12 gap-3 md:gap-4">
          {GALLERY_IMAGES.slice(0, 5).map(({ src, alt }, i) => (
            <div
              key={src}
              className={`group relative overflow-hidden bg-carbon-700 transition-[opacity,transform,filter] duration-1000 ease-out-expo ${STAGGER[i]} ${
                i === 0
                  ? "col-span-12 md:col-span-8 aspect-[16/10]"
                  : i === 1
                    ? "col-span-12 md:col-span-4 aspect-[16/10] md:aspect-auto"
                    : "col-span-12 sm:col-span-4 aspect-[4/3] sm:aspect-[3/4]"
              } ${gridIn ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-sm"}`}
            >
              <img
                src={src}
                alt={alt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out-expo group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-carbon-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 pb-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out-expo">
                <span className="font-mono text-2xs tracking-[0.25em] uppercase text-carbon-50">{alt}</span>
                <span className="font-mono text-2xs text-gold-500">0{i + 1}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-10 flex justify-end ${reveal(gridIn)} [transition-delay:0.5s]`}>
          <TransitionLink
            to="/gallery"
            className="group inline-flex items-center gap-4 border border-carbon-600 text-titanium-400 font-display font-bold text-sm tracking-widest uppercase px-8 py-4 hover:border-gold-500 hover:text-carbon-50 transition-all duration-300 ease-out-expo"
          >
            View the portfolio
            <span aria-hidden className="transition-transform duration-300 ease-out-expo group-hover:translate-x-1.5">-&gt;</span>
          </TransitionLink>
        </div>
      </div>
    </section>
  );
}

function BuilderCtaSection() {
  const { ref, inView } = useInView<HTMLDivElement>(0.15, "home-builder-cta");
  const { content } = useContent();
  const { home } = content;

  return (
    <section className="py-40 bg-carbon-900 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={home.builderImage}
          alt=""
          aria-hidden
          className={`h-full w-full object-cover transition-transform duration-[2.5s] ease-out-expo ${inView ? "scale-100" : "scale-110"}`}
        />
      </div>
      <div className="absolute inset-0 bg-carbon-900/80" />

      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center font-display font-extrabold uppercase tracking-tight [font-stretch:125%] text-[22vw] leading-none text-transparent [-webkit-text-stroke:1px_rgba(201,168,76,0.1)] select-none"
      >
        Yours
      </span>

      <div ref={ref} className="relative z-10 max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-8">
        <h2 className={`font-display font-bold text-5xl md:text-6xl text-carbon-50 uppercase leading-tight ${reveal(inView)} [transition-delay:0s]`}>
          {home.builderTitle}
          <br />
          <span className="bg-gradient-gold bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer-slow motion-reduce:animate-none">
            {home.builderAccent}
          </span>
        </h2>
        <p className={`text-green-100 text-lg max-w-xl ${reveal(inView)} [transition-delay:0.15s]`}>
          {home.builderSubtitle}
        </p>
        <TransitionLink
          to="/builder"
          className={`group inline-flex items-center gap-3 bg-gold-500 text-carbon-900 font-display font-bold text-sm tracking-widest uppercase px-10 py-5 hover:bg-gold-400 transition-all duration-300 ease-out-expo animate-pulse-gold motion-reduce:animate-none ${reveal(inView)} [transition-delay:0.3s]`}
        >
          {home.builderCtaText}
          <span aria-hidden className="transition-transform duration-300 ease-out-expo group-hover:translate-x-1.5">-&gt;</span>
        </TransitionLink>
      </div>
    </section>
  );
}

const FEATURED_PRODUCTS = [
  ...(PRODUCT_CATEGORIES.find((c) => c.id === "frames")?.products ?? []),
];

const MATERIAL_LABEL: Record<string, string> = {
  titanium: "Titanium",
  carbon: "Carbon",
  aluminium: "Aluminium",
  steel: "Steel",
  alloy: "Alloy",
};

function ShopSection() {
  const { ref: headerRef, inView: headerIn } = useInView<HTMLDivElement>(0.15, "home-shop-header");
  const { ref: cardsRef, inView: cardsIn } = useInView<HTMLDivElement>(0.1, "home-shop-cards");

  return (
    <section id="shop" className="py-32 bg-carbon-900">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={headerRef}>
          <p className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 ${reveal(headerIn)} [transition-delay:0s]`}>
            The Range
          </p>
          <h2 className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase mb-4 ${reveal(headerIn)} [transition-delay:0.1s]`}>
            Four expressions.
            <br />
            <span className="text-titanium-400">One obsession.</span>
          </h2>
          <p className={`text-titanium-400 text-lg mb-16 max-w-xl ${reveal(headerIn)} [transition-delay:0.2s]`}>
            Each line is a point of departure - a platform perfected for its terrain, then composed entirely around its rider.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {FEATURED_PRODUCTS.map((product, i) => {
            const branding = product.builderFrameId ? BIKE_LINE_BRANDING[product.builderFrameId] : undefined;
            const isComingSoon = isComingSoonFrame(product.builderFrameId);

            return (
              <div
                key={product.id}
                className={`group relative flex flex-col bg-carbon-800 border border-carbon-700 hover:border-gold-500/50 hover:shadow-card-hover hover:-translate-y-1 transition-[opacity,transform,border-color,box-shadow,filter] duration-700 ease-out-expo ${STAGGER[i]} ${
                  cardsIn ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-sm"
                }`}
              >
                <span aria-hidden className="pointer-events-none absolute top-3 left-3 z-10 w-5 h-5 border-t border-l border-gold-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span aria-hidden className="pointer-events-none absolute bottom-3 right-3 z-10 w-5 h-5 border-b border-r border-gold-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute top-4 right-4 z-10 font-mono text-2xs tracking-[0.25em] text-titanium-500 group-hover:text-gold-500 transition-colors duration-300">
                  0{i + 1}
                </span>

                <div className="aspect-[4/3] overflow-hidden bg-carbon-700">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out-expo group-hover:scale-105"
                    />
                  ) : (
                    <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-b from-carbon-800 to-carbon-900">
                      <span
                        aria-hidden
                        className="font-display font-extrabold italic [font-stretch:125%] uppercase tracking-tight text-4xl text-carbon-50/[0.06] select-none transition-transform duration-700 ease-out-expo group-hover:scale-110"
                      >
                        {branding?.mark ?? product.name}
                      </span>
                      <span className="absolute bottom-3 right-4 font-mono text-2xs tracking-widest uppercase text-titanium-700">
                        {isComingSoon ? "Coming soon" : "Photography coming soon"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="flex flex-col gap-2">
                    <span className="font-mono text-2xs tracking-[0.3em] uppercase text-gold-500">
                      {MATERIAL_LABEL[product.material] ?? product.material}
                    </span>
                    {isComingSoon && (
                      <span className="self-start border border-gold-500/50 bg-gold-500/10 px-3 py-1 font-mono text-2xs tracking-[0.25em] uppercase text-gold-500">
                        Coming soon
                      </span>
                    )}
                    {branding ? (
                      <ModelLockup branding={branding} size="sm" showEyebrow={false} as="h3" />
                    ) : (
                      <h3 className="font-display font-bold text-lg text-carbon-50 uppercase">{product.name}</h3>
                    )}
                    <p className="font-mono text-xs text-titanium-500">{product.spec}</p>
                  </div>
                  <p className="text-titanium-400 text-sm leading-relaxed flex-1 line-clamp-2">{product.description}</p>
                  <TransitionLink
                    to={`/bikes/${product.builderFrameId ?? "raptor-i"}`}
                    className="self-start inline-flex items-center gap-2 text-titanium-500 hover:text-gold-500 font-mono text-xs tracking-widest uppercase transition-colors duration-200"
                  >
                    {isComingSoon ? "Coming soon" : "Discover"} -&gt;
                  </TransitionLink>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-8">
          <TransitionLink
            to="/products"
            className="group inline-flex items-center gap-3 border border-carbon-600 text-titanium-400 font-display font-bold text-xs tracking-widest uppercase px-6 py-3 hover:border-gold-500 hover:text-carbon-50 transition-all duration-300 ease-out-expo"
          >
            Explore the range
            <span aria-hidden className="transition-transform duration-300 ease-out-expo group-hover:translate-x-1.5">-&gt;</span>
          </TransitionLink>
        </div>
      </div>
    </section>
  );
}

type FormState = "idle" | "submitting" | "success" | "error";

function ContactSection() {
  const { ref, inView } = useInView<HTMLDivElement>(0.15, "home-contact");
  const { content } = useContent();
  const { contactEmail } = content.siteSettings;
  const [formState, setFormState] = useState<FormState>("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState("submitting");

    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          message: data.get("message"),
        }),
      });

      if (res.ok) {
        setFormState("success");
        form.reset();
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <section id="contact" className="relative py-32 bg-carbon-800 texture-noise">
      <div ref={ref} className="relative max-w-2xl mx-auto px-6">
        <p className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 ${reveal(inView)} [transition-delay:0s]`}>
          The Enquiry
        </p>
        <h2 className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase mb-4 ${reveal(inView)} [transition-delay:0.1s]`}>
          Begin the conversation.
        </h2>
        <p className={`text-titanium-400 text-lg mb-12 ${reveal(inView)} [transition-delay:0.2s]`}>
          Every commission begins with a conversation. Ask about a build, a timeline, or a single detail - you will hear back personally.
        </p>

        {formState === "success" ? (
          <div className={`border border-carbon-700 bg-carbon-900 p-10 flex flex-col gap-5 ${reveal(inView)} [transition-delay:0.35s]`}>
            <div className="w-10 h-10 border border-gold-500 flex items-center justify-center shrink-0 animate-pulse-gold motion-reduce:animate-none">
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M1 6l4.5 4.5L15 1" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="font-display font-bold text-xl text-carbon-50 uppercase">Enquiry received.</h3>
              <p className="text-titanium-400 text-sm leading-relaxed">
                Thank you. Kieran will respond personally within 48 hours.
              </p>
            </div>
            <button
              onClick={() => setFormState("idle")}
              className="self-start text-titanium-500 hover:text-gold-500 font-mono text-xs tracking-widest uppercase transition-colors duration-150"
            >
              Send another -&gt;
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={`flex flex-col gap-5 ${reveal(inView)} [transition-delay:0.35s]`}>
            {[
              { id: "name", label: "Name", type: "text", placeholder: "Your name" },
              { id: "email", label: "Email", type: "email", placeholder: "your@email.com" },
            ].map(({ id, label, type, placeholder }) => (
              <div key={id} className="flex flex-col gap-2">
                <label htmlFor={id} className="text-sm font-display tracking-wider uppercase text-titanium-400">
                  {label}
                </label>
                <input
                  id={id}
                  name={id}
                  type={type}
                  required
                  placeholder={placeholder}
                  className="bg-carbon-700 border border-carbon-600 text-carbon-50 placeholder-carbon-400 px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors duration-150"
                />
              </div>
            ))}

            <div className="flex flex-col gap-2">
              <label htmlFor="message" className="text-sm font-display tracking-wider uppercase text-titanium-400">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                placeholder="Tell us about the machine you have in mind..."
                className="bg-carbon-700 border border-carbon-600 text-carbon-50 placeholder-carbon-400 px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors duration-150 resize-none"
              />
            </div>

            {formState === "error" && (
              <p className="text-red-400 font-mono text-xs tracking-wider">
                Something went wrong. Try emailing directly at {contactEmail}
              </p>
            )}

            <button
              type="submit"
              disabled={formState === "submitting"}
              className="self-start inline-flex items-center gap-4 bg-gold-500 text-carbon-900 font-display font-bold text-sm tracking-widest uppercase px-8 py-4 hover:bg-gold-400 transition-colors duration-150 shadow-glow-gold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formState === "submitting" ? (
                <>
                  <span className="w-4 h-4 border-2 border-carbon-900 border-t-transparent rounded-full animate-spin" />
                  Sending
                </>
              ) : (
                "Send enquiry ->"
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <>
      <HeroSection />
      <MarqueeStrip />
      <ManifestoSection />
      <AboutSection />
      <CraftSection />
      <ShopSection />
      <GallerySection />
      <CommissionJourneySection />
      <BuilderCtaSection />
      <ContactSection />
    </>
  );
}
